import React, { useEffect, useState } from 'react';
import Hero from '../../components/Hero/Hero';
import Container from '../../components/Container/Container';
import MarketCard from '../../components/MarketCard/MarketCard';
import { useHistory } from 'react-router-dom';
import Modal from '../../components/Modal/Modal';
import styled from 'styled-components';
import {
  BreakpointUpLarge,
  BreakpointUpMedium,
} from '../../styles/Breakpoints';
import { marketService } from '../../services/market.service';
import { useTranslation } from 'react-i18next';

const MARKET_HIGHLIGHTS = [
  'Live on Polygon',
  'UMA Protocol Resolution',
  'Real-Time Markets',
];

const BET_STEPS = [
  {
    emoji: '📝',
    title: 'Bet Creation',
    description: 'Create a bet in plain language with your terms, optional odds, and clear resolution criteria.',
    example: '"The Miami Heat will win their next game vs the Knicks on March 25, 2026."',
    highlights: ['Natural language terms', 'Optional odds', 'Resolution criteria'],
  },
  {
    emoji: '🤖',
    title: 'AI Validation',
    description: 'Before going live, AI validates every bet for objectivity, verifiability, and clarity.',
    highlights: ['Objectively verifiable?', 'Clear data source?', 'Binary or quantifiable?'],
    statuses: [
      { label: 'Resolvable', icon: '✅', tone: 'success' },
      { label: 'Not resolvable', icon: '❌', tone: 'danger' },
      { label: 'Needs clarification', icon: '⚠️', tone: 'secondary' },
    ],
  },
  {
    emoji: '🏛',
    title: 'Match',
    description: 'Approved public bets enter a public lobby. Others can accept, counter with different odds, or pass. Option to create private bets.',
    highlights: ['Public lobby', 'Private bets', 'Accept as-is', 'Counter with odds'],
  },
  {
    emoji: '🤝',
    title: 'Escrow',
    description: 'Once matched, both sides fund escrow. Terms are locked — no edits allowed.',
    highlights: ['USDC / fiat escrow', 'Terms locked on match', 'No edits post-match'],
  },
  {
    emoji: '🧾',
    title: 'Resolution Trigger',
    description: 'The system monitors the event or waits for a trigger. AI evaluates the outcome using predefined sources.',
    highlights: ['Automatic triggers', 'ESPN, APIs, web data', 'AI-powered evaluation'],
  },
  {
    emoji: '💰',
    title: 'Payout Flow',
    description: 'AI determines the winner and allocates funds. A 24-hour dispute window opens for fairness.',
    highlights: ['Instant allocation', '24-hour dispute window', 'Transparent process'],
  },
  {
    emoji: '⚖️',
    title: 'Dispute Layer',
    description: 'Disputed outcomes escalate to third-party arbitration or a decentralized jury for final ruling.',
    highlights: ['Third-party arbitration', 'Decentralized jury (future)', 'Final payout executed'],
  },
  {
    emoji: '🏆',
    title: 'Winner Paid Out',
    description: 'Once resolved or dispute settled, the winner receives the full payout directly to their wallet.',
    highlights: ['Instant transfer', 'Direct to wallet', 'No middlemen'],
  },
];

const COMING_SOON_MARKETS = [
  // {
  //   id: '900001',
  //   permalink: null,
  //   market_resolved: 0,
  //   marketWeight: 'Coming Soon',
  //   isComingSoon: true,
  //   image_url: '/markets/russia-ukraine-ceasefire.jpg',
  //   options: [
  //     { id: '9000011', marginal_price: null },
  //     { id: '9000012', marginal_price: null }
  //   ]
  // }
];

const Home = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [showCountryBlockedPopUp, setShowCountryBlockedPopUp] = useState(0);
  const [cards, setCards] = useState([]);
  const [hoveredStep, setHoveredStep] = useState(null);

  useEffect(() => {
    const onLoad = async () => {
      marketService.getPublishedMarkets()
        .then((data) => {
          const liveMarkets = Array.isArray(data) ? data : [];
          setCards([...liveMarkets, ...COMING_SOON_MARKETS]);
        }, (error) => {
          console.log(error);
        });
    };

    onLoad();
  }, []);

  const cardClicked = (e, link, item) => {
    if (item?.isComingSoon || !link) {
      return;
    }

    history.push(link);
  };

  return (
    <>
      <Hero
        marginBottom={18}
        title="Bet on everything with PolyBet"
        description="Bet on everything with PolyBet from finance and politics to current events, sports, and everyday life."
      />

      <Container>
        <MarketsSection>
          <SectionTitle>{t('home.markets')}</SectionTitle>
          <MarketsGrid>
            {cards.map((item) => (
              <MarketCard
                marginBottom={0}
                item={item}
                key={item.id}
                to={item.permalink ? `market/${item.permalink}` : undefined}
                onMarketClick={cardClicked}
              />
            ))}
          </MarketsGrid>
          <MarketsIntroCard>
            <MarketsIntroEyebrow>
              <LiveDot aria-hidden="true" />
              Live Now
            </MarketsIntroEyebrow>
            <MarketsIntroTitle>PolyBet Markets</MarketsIntroTitle>
            <MarketsIntroCopy>
              PolyBet is a prediction market platform live on the Polygon network. Trade on
              real-world outcomes — from politics to sports — with markets resolved transparently
              through the UMA optimistic oracle protocol.
            </MarketsIntroCopy>
            <MarketsIntroHighlights>
              {MARKET_HIGHLIGHTS.map((highlight) => (
                <HeroHighlightPill key={highlight}>{highlight}</HeroHighlightPill>
              ))}
            </MarketsIntroHighlights>
          </MarketsIntroCard>
        </MarketsSection>
      </Container>

      <Container>
        <BetsSection>
          <BetsInner>
            <BetsTitle>
              PolyBet Bets <BetsTitleAccent>Coming Soon</BetsTitleAccent>
            </BetsTitle>
            <BetsCopy>
              Peer-to-peer wagering, validated by AI. No order books. No market makers. Just find
              someone who disagrees — and lock it in.
            </BetsCopy>

            <Timeline>
              <TimelineLine>
                <TimelineProgress width={hoveredStep !== null ? `${((hoveredStep + 1) / BET_STEPS.length) * 100}%` : '0%'} />
              </TimelineLine>

              <TimelineItems>
                {BET_STEPS.map((step, index) => (
                  <TimelineItem
                    key={step.title}
                    onMouseEnter={() => setHoveredStep(index)}
                    onMouseLeave={() => setHoveredStep(null)}
                  >
                    <TimelineDot isActive={hoveredStep === index}>{step.emoji}</TimelineDot>
                    <TimelineLabel isActive={hoveredStep === index}>{step.title}</TimelineLabel>
                  </TimelineItem>
                ))}
              </TimelineItems>
            </Timeline>

            <StepsGrid>
              {BET_STEPS.map((step, index) => (
                <StepCard key={step.title} style={{ animationDelay: `${index * 0.08}s` }}>
                  <StepGradient />
                  <StepBody>
                    <StepHeader>
                      <StepBadge>{step.emoji}</StepBadge>
                      <div>
                        <StepMeta>{`Step ${index + 1}`}</StepMeta>
                        <StepTitle>{step.title}</StepTitle>
                      </div>
                    </StepHeader>
                    <StepDescription>{step.description}</StepDescription>
                    {step.example && <StepExample>{step.example}</StepExample>}
                    {step.statuses && (
                      <StatusRow>
                        {step.statuses.map((status) => (
                          <StatusPill key={status.label} tone={status.tone}>
                            <span>{status.icon}</span>
                            {status.label}
                          </StatusPill>
                        ))}
                      </StatusRow>
                    )}
                    <Highlights>
                      {step.highlights.map((highlight) => (
                        <HighlightTag key={highlight}>{highlight}</HighlightTag>
                      ))}
                    </Highlights>
                  </StepBody>
                </StepCard>
              ))}
              <CustomBetsCard style={{ animationDelay: `${BET_STEPS.length * 0.08}s` }}>
                <StepGradient />
                <StepBody>
                  <CustomBadge>Under Development</CustomBadge>
                  <StepHeader>
                    <StepBadge>🚧</StepBadge>
                    <div>
                      <StepMeta>Coming Soon</StepMeta>
                      <StepTitle>Custom Bets</StepTitle>
                    </div>
                  </StepHeader>
                  <StepDescription>
                    Custom bets are coming soon to PolyBet and are currently under development. Join
                    the PolyBet community to stay up to date on personal bets.
                  </StepDescription>
                  <JoinCommunityLink
                    href="https://discord.gg/polybet"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Join Community
                    <span>→</span>
                  </JoinCommunityLink>
                </StepBody>
              </CustomBetsCard>
            </StepsGrid>
          </BetsInner>
        </BetsSection>
      </Container>

      {showCountryBlockedPopUp === 1 && (
        <Modal
          title={t('restricted_country_message')}
          align="center"
          close={() => setShowCountryBlockedPopUp(0)}
        >
          <div>
            <p style={{ wordWrap: 'break-word', wordBreak: 'break-all' }}>
              {t('restricted_country_message')}
            </p>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Home;

const MarketsSection = styled.section`
  padding: 6px 0 44px;
`;

const SectionTitle = styled.h2`
  margin: 0 0 32px;
  color: hsl(210 40% 98%);
  text-align: center;
  font-size: 36px;
  font-weight: 700;
`;

const MarketsIntroCard = styled.div`
  position: relative;
  overflow: hidden;
  margin: 40px auto 0;
  max-width: 1152px;
  padding: 32px;
  border: 1px solid hsl(222 47% 15%);
  border-radius: 12px;
  background: linear-gradient(135deg, hsl(222 47% 8% / 0.8), hsl(222 47% 10% / 0.6));
  text-align: center;
  backdrop-filter: blur(8px);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, hsl(263 70% 60%), hsl(190 95% 45%));
    opacity: 0.05;
    pointer-events: none;
  }

  ${BreakpointUpMedium} {
    padding: 40px;
  }
`;

const MarketsIntroEyebrow = styled.span`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  color: hsl(142 71% 45%);
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.1em;
  line-height: 1.25rem;
  text-transform: uppercase;
`;

const LiveDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: currentColor;
  box-shadow: 0 0 0 0 hsl(142 71% 45% / 0.35);
  animation: pulseLive 1.8s ease-in-out infinite;

  @keyframes pulseLive {
    0% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 hsl(142 71% 45% / 0.35);
    }

    70% {
      transform: scale(1);
      box-shadow: 0 0 0 10px hsl(142 71% 45% / 0);
    }

    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 hsl(142 71% 45% / 0);
    }
  }
`;

const MarketsIntroTitle = styled.h2`
  position: relative;
  z-index: 1;
  margin: 0 0 12px;
  color: hsl(210 40% 98%);
  font-size: 30px;
  font-weight: 700;
  line-height: 1.1;

  ${BreakpointUpMedium} {
    font-size: 36px;
  }
`;

const MarketsIntroCopy = styled.p`
  position: relative;
  z-index: 1;
  max-width: 672px;
  margin: 0 auto 24px;
  color: hsl(215 20% 65%);
  font-size: 18px;
  line-height: 1.625;
`;

const MarketsIntroHighlights = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  position: relative;
  z-index: 1;
`;

const HeroHighlightPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border: 1px solid hsl(222 47% 16%);
  border-radius: 999px;
  background: hsl(222 47% 12% / 0.4);
  color: hsl(215 20% 65%);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.25rem;
  backdrop-filter: blur(8px);
`;

const MarketsGrid = styled.div`
  display: grid;
  gap: 24px;
  justify-content: center;

  ${BreakpointUpMedium} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  ${BreakpointUpLarge} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const BetsSection = styled.section`
  padding: 40px 0 68px;
  text-align: center;
`;

const BetsInner = styled.div`
  max-width: 1152px;
  margin: 0 auto;
`;

const BetsTitle = styled.h2`
  margin: 0 0 16px;
  color: hsl(210 40% 98%);
  font-size: 36px;
  font-weight: 700;

  ${BreakpointUpMedium} {
    font-size: 48px;
  }
`;

const BetsTitleAccent = styled.span`
  color: hsl(263 70% 60%);
`;

const BetsCopy = styled.p`
  max-width: 672px;
  margin: 0 auto 48px;
  color: hsl(215 20% 65%);
  font-size: 18px;
  line-height: 1.7;
`;

const Timeline = styled.div`
  position: relative;
  margin: 0 auto 64px;
`;

const TimelineLine = styled.div`
  position: absolute;
  top: 24px;
  left: 0;
  right: 0;
  display: none;
  height: 2px;
  transform: translateY(-50%);
  background: hsl(222 47% 15%);

  ${BreakpointUpMedium} {
    display: block;
  }
`;

const TimelineProgress = styled.div`
  height: 100%;
  background: linear-gradient(90deg, hsl(263 70% 60%), hsl(190 95% 45%));
  transition: width 0.5s ease;
  width: ${({ width }) => width};
`;

const TimelineItems = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;

  ${BreakpointUpMedium} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const TimelineItem = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const TimelineDot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border: 2px solid ${({ isActive }) => (isActive ? 'hsl(263 70% 60%)' : 'hsl(222 47% 15%)')};
  border-radius: 999px;
  background: ${({ isActive }) => (isActive ? 'hsl(263 70% 60% / 0.2)' : 'hsl(222 47% 8%)')};
  font-size: 18px;
  transition: all 0.3s ease;
`;

const TimelineLabel = styled.span`
  color: ${({ isActive }) => (isActive ? 'hsl(263 70% 60%)' : 'hsl(215 20% 65%)')};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition: color 0.3s ease;
`;

const StepsGrid = styled.div`
  display: grid;
  gap: 24px;

  ${BreakpointUpMedium} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  ${BreakpointUpLarge} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const StepCard = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  overflow: hidden;
  padding: 24px;
  border: 1px solid hsl(222 47% 15%);
  border-radius: 12px;
  background: linear-gradient(135deg, hsl(222 47% 8% / 0.8), hsl(222 47% 10% / 0.6));
  backdrop-filter: blur(8px);
  transition: box-shadow 0.3s ease;
  text-align: left;
  animation: fadeUp 0.4s ease both;

  &:hover {
    box-shadow: 0 0 40px hsl(263 70% 60% / 0.3);
  }

  @keyframes fadeUp {
    from {
      opacity: 0;
      transform: translateY(8px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const StepGradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, hsl(263 70% 60%), hsl(190 95% 45%));
  opacity: 0;
  transition: opacity 0.3s ease;

  ${StepCard}:hover & {
    opacity: 0.05;
  }
`;

const StepBody = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const StepHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;

const StepBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: hsl(263 70% 60% / 0.1);
  font-size: 18px;
  flex-shrink: 0;
`;

const StepMeta = styled.div`
  color: hsl(215 20% 65%);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const StepTitle = styled.h3`
  margin: 0;
  color: hsl(210 40% 98%);
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
`;

const StepDescription = styled.p`
  margin: 0 0 16px;
  color: hsl(215 20% 65%);
  font-size: 14px;
  line-height: 1.65;
  flex: 1;
`;

const StepExample = styled.div`
  margin: 0 0 16px;
  padding: 12px 14px;
  border: 1px solid hsl(222 47% 15%);
  border-radius: 12px;
  background: hsl(222 47% 10% / 0.45);
  color: hsl(215 20% 65%);
  font-size: 12px;
  font-style: italic;
  line-height: 1.6;
`;

const StatusRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 16px;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: 1px solid hsl(222 47% 15%);
  border-radius: 999px;
  background: hsl(222 47% 10% / 0.45);
  color: ${({ tone }) => {
    if (tone === 'success') return 'hsl(142 71% 45%)';
    if (tone === 'danger') return 'hsl(0 84% 60%)';
    return 'hsl(190 95% 45%)';
  }};
  font-size: 11px;
  font-weight: 600;
`;

const Highlights = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const HighlightTag = styled.span`
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 999px;
  background: hsl(222 47% 12%);
  color: hsl(215 20% 65%);
  font-size: 11px;
  font-weight: 500;
`;

const CustomBetsCard = styled(StepCard)`
  border: 2px dashed hsl(263 70% 60% / 0.35);

  &:hover {
    border-color: hsl(263 70% 60% / 0.6);
  }
`;

const CustomBadge = styled.span`
  display: inline-flex;
  align-self: flex-start;
  margin-bottom: 16px;
  padding: 6px 12px;
  border-radius: 999px;
  background: hsl(190 95% 45% / 0.12);
  color: hsl(190 95% 45%);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const JoinCommunityLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: auto;
  align-self: flex-start;
  padding: 10px 16px;
  border-radius: 999px;
  background: hsl(263 70% 60% / 0.12);
  color: hsl(263 70% 60%);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s ease, opacity 0.2s ease;

  &:hover,
  &:focus-visible {
    background: hsl(263 70% 60% / 0.2);
    opacity: 1;
    text-decoration: none;
  }
`;
